import * as React from 'react';
import { flow } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import CircularProgress from '@mui/material/CircularProgress';
import format from 'date-fns/format';
import html2canvas from 'html2canvas';
import { PDFDocument } from 'pdf-lib';
import { jsPDF } from 'jspdf';

import ROUTES from '@giro-pdv/constants/routes.constant';

import useDialogHook from '@giro-pdv/hooks/useDialogRedux.hook';

import dialogReceipt from '../store/dialogReceipt';
import receiptStore from '@giro-pdv/modules/receipt/store/receipt';

export default function DialogReceiptComponent() {
  const history = useHistory();
  const dispatch = useDispatch();

  const dispatchRedux = {
    serviceGet: flow(dialogReceipt.action.serviceGet, dispatch),
    reset: flow(dialogReceipt.action.reset, dispatch),
    fetchEnd: flow(receiptStore.action.fetchEnd, dispatch),
  };

  const selectorRedux = {
    data: useSelector(dialogReceipt.selector.selectData),
    loading: useSelector(dialogReceipt.selector.selectLoading),
  };

  const { handleClose, visible } = useDialogHook(dialogReceipt);

  React.useEffect(() => {
    if (visible) {
      dispatchRedux.serviceGet();
    } else {
      dispatchRedux.reset();
    }
  }, [visible]);

  const downloadDocument = async () => {
    const input: any = document.getElementById('divToPrint');

    const imgData = await html2canvas(input);

    const pdf = new jsPDF('p', 'mm', [80, 297]);
    const width = pdf.internal.pageSize.getWidth();
    const height = pdf.internal.pageSize.getHeight();

    pdf.addImage(imgData, 'PNG', 7.5, 7.5, width, height - 120);

    const blob = pdf.output('arraybuffer');

    const pdfNew = await PDFDocument.load(blob);

    const linkNewPdf = await pdfNew.saveAsBase64({ dataUri: true });
    const nameSource = `comprovante_${
      selectorRedux?.data[0].idTransacao
    }_${+Date.now()}.pdf`;

    const downloadLink = document.createElement('a');

    downloadLink.href = linkNewPdf;
    downloadLink.download = nameSource;
    downloadLink.click();
  };

  const printDocument = async () => {
    const input: any = document.getElementById('divToPrint');

    const imgData = await html2canvas(input);

    const pdf = new jsPDF('p', 'mm', [80, 297]);
    const width = pdf.internal.pageSize.getWidth();
    const height = pdf.internal.pageSize.getHeight();

    pdf.addImage(imgData, 'PNG', 7.5, 7.5, width, height - 120);

    pdf.autoPrint();

    window.open(URL.createObjectURL(pdf.output('blob')));
  };

  const handleRedirectToReceipt = () => {
    dispatchRedux.fetchEnd(selectorRedux.data);

    dispatchRedux.reset();

    history.push(ROUTES.RECEIPT.ROOT);
  };

  const receipt = selectorRedux?.data;

  return (
    <Dialog open={visible} onClose={handleClose}>
      <DialogTitle>
        <Box display="flex" alignItems="center">
          <Typography variant="trasso_body_medium" color="trasso.purple.80">
            Recibo da transação
          </Typography>
        </Box>
      </DialogTitle>
      <Box
        minWidth={400}
        display="flex"
        flexDirection="column"
        gap={2}
        px={3}
        pb={3}
        position="relative"
      >
        <Box display="flex" flexDirection="column" gap={2} id="divToPrint">
          <Box display="flex" flexDirection="column" gap={1}>
            <Typography variant="trasso_body_medium">
              GIRO PAGAMENTOS
            </Typography>
            <Typography variant="trasso_body_small">COMPROVANTE</Typography>
            <Typography variant="trasso_body_small">
              VIA ESTABELECIMENTO
            </Typography>
            {selectorRedux?.data && (
              <Box display="flex" flexDirection="column">
                <Box display="flex" flexDirection="column">
                  <Typography variant="trasso_body_small">
                    {selectorRedux?.data[0]?.bandeira}
                  </Typography>
                  <Typography variant="trasso_body_small">
                    {selectorRedux?.data[0]?.binCartao}
                  </Typography>
                </Box>
                <Box display="flex" flexDirection="column">
                  <Typography variant="trasso_body_small">
                    {selectorRedux?.data[0]?.softDescriptor}
                  </Typography>
                  <Typography variant="trasso_body_small">
                    {selectorRedux?.data[0]?.nuCPFCNPJ}
                  </Typography>
                </Box>
                <Typography variant="trasso_body_small">
                  {selectorRedux?.data[0]?.cidade}
                </Typography>
                <Box display="flex" flexDirection="row" gap={0.5}>
                  <Typography variant="trasso_body_small">
                    {format(
                      new Date(
                        selectorRedux?.data[0]?.dtTransacao || Date.now()
                      ),
                      'dd/MM/yyyy'
                    )}
                  </Typography>
                  <Typography variant="trasso_body_small">
                    {format(
                      new Date(
                        selectorRedux?.data[0]?.dtTransacao || Date.now()
                      ),
                      'HH:mm'
                    )}
                  </Typography>
                </Box>
                <Typography variant="trasso_body_small">
                  {selectorRedux?.data[0]?.tipoPagamento}{' '}
                  {selectorRedux?.data[0]?.parcelas}x
                </Typography>
                <Typography variant="trasso_body_small">
                  VALOR:{' '}
                  {Number(selectorRedux?.data[0]?.vlTransacao).toLocaleString(
                    'pt-br',
                    {
                      currency: 'BRL',
                      style: 'currency',
                    }
                  )}
                </Typography>
                <Typography variant="trasso_body_small">
                  AUTORIZAÇÃO: {selectorRedux?.data[0]?.idTransacao}
                </Typography>

                {receipt &&
                  receipt.map((a) => {
                    const isBillet = a?.tipoTributo?.toUpperCase() === 'BOLETO';
                    return (
                      isBillet && (
                        <Box key={a?.idTransacao} pt={3}>
                          <Box display="flex" flexDirection="column">
                            <Typography variant="trasso_body_small">
                              BOLETO: {a?.referenciaTributo}
                            </Typography>
                            <Typography variant="trasso_body_small">
                              VALOR TRIBUTO:{' '}
                              {Number(a?.valorTributo).toLocaleString('pt-br', {
                                currency: 'BRL',
                                style: 'currency',
                              })}
                            </Typography>
                            <Typography variant="trasso_body_small">
                              VENC. TRIBUTO:{' '}
                              {format(
                                new Date(a?.vencimentoTributo),
                                'dd/MM/yyyy'
                              )}{' '}
                            </Typography>
                            <Typography variant="trasso_body_small">
                              AUTENTICAÇÃO:
                            </Typography>
                            <Typography variant="trasso_body_small">
                              {a?.autenticacaoPagamento}
                            </Typography>
                            {a?.cdBarras && (
                              <Typography variant="trasso_body_small">
                                COD.BARRAS: {a?.cdBarras}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      )
                    );
                  })}
              </Box>
            )}
          </Box>
          <Divider />
          <Box display="flex" flexDirection="column" gap={1}>
            <Typography variant="trasso_body_medium">
              GIRO PAGAMENTOS
            </Typography>
            <Typography variant="trasso_body_small">COMPROVANTE</Typography>
            <Typography variant="trasso_body_small">VIA CLIENTE</Typography>
            {selectorRedux?.data && (
              <Box display="flex" flexDirection="column">
                <Box display="flex" flexDirection="column">
                  <Typography variant="trasso_body_small">
                    {selectorRedux?.data[0]?.bandeira}
                  </Typography>
                  <Typography variant="trasso_body_small">
                    {selectorRedux?.data[0]?.binCartao}
                  </Typography>
                </Box>
                <Box display="flex" flexDirection="column">
                  <Typography variant="trasso_body_small">
                    {selectorRedux?.data[0]?.softDescriptor}
                  </Typography>
                  <Typography variant="trasso_body_small">
                    {selectorRedux?.data[0]?.nuCPFCNPJ}
                  </Typography>
                </Box>
                <Typography variant="trasso_body_small">
                  {selectorRedux?.data[0]?.cidade}
                </Typography>
                <Box display="flex" flexDirection="row" gap={0.5}>
                  <Typography variant="trasso_body_small">
                    {format(
                      new Date(
                        selectorRedux?.data[0]?.dtTransacao || Date.now()
                      ),
                      'dd/MM/yyyy'
                    )}
                  </Typography>
                  <Typography variant="trasso_body_small">
                    {format(
                      new Date(
                        selectorRedux?.data[0]?.dtTransacao || Date.now()
                      ),
                      'HH:mm'
                    )}
                  </Typography>
                </Box>
                <Typography variant="trasso_body_small">
                  {selectorRedux?.data[0]?.tipoPagamento}{' '}
                  {selectorRedux?.data[0]?.parcelas}x
                </Typography>
                <Typography variant="trasso_body_small">
                  VALOR:{' '}
                  {Number(selectorRedux?.data[0]?.vlTransacao).toLocaleString(
                    'pt-br',
                    {
                      currency: 'BRL',
                      style: 'currency',
                    }
                  )}
                </Typography>
                <Typography variant="trasso_body_small">
                  AUTORIZAÇÃO: {selectorRedux?.data[0]?.idTransacao}
                </Typography>

                {receipt &&
                  receipt.map((a) => {
                    const isBillet = a?.tipoTributo?.toUpperCase() === 'BOLETO';
                    return (
                      isBillet && (
                        <Box key={a?.idTransacao} pt={3}>
                          <Box display="flex" flexDirection="column">
                            <Typography variant="trasso_body_small">
                              BOLETO: {a?.referenciaTributo}
                            </Typography>
                            <Typography variant="trasso_body_small">
                              VALOR TRIBUTO:{' '}
                              {Number(a?.valorTributo).toLocaleString('pt-br', {
                                currency: 'BRL',
                                style: 'currency',
                              })}
                            </Typography>
                            <Typography variant="trasso_body_small">
                              VENC. TRIBUTO:{' '}
                              {format(
                                new Date(a?.vencimentoTributo),
                                'dd/MM/yyyy'
                              )}{' '}
                            </Typography>
                            <Typography variant="trasso_body_small">
                              AUTENTICAÇÃO:
                            </Typography>
                            <Typography variant="trasso_body_small">
                              {a?.autenticacaoPagamento}
                            </Typography>
                            {a?.cdBarras && (
                              <Typography variant="trasso_body_small">
                                COD.BARRAS: {a?.cdBarras}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      )
                    );
                  })}
              </Box>
            )}
          </Box>
        </Box>
        <Button variant="contained" onClick={printDocument}>
          Imprimir comprovante
        </Button>
        <Button variant="contained" onClick={downloadDocument}>
          Baixar comprovante
        </Button>
        <Button variant="outlined" onClick={handleRedirectToReceipt}>
          Baixar recibo
        </Button>
        <Button variant="outlined" onClick={handleClose}>
          Fechar
        </Button>

        {selectorRedux.loading && (
          <Box
            position="absolute"
            top={0}
            left={0}
            width="100%"
            height="100%"
            bgcolor="white"
            alignItems="center"
            justifyContent="center"
            display="flex"
          >
            <CircularProgress />
          </Box>
        )}
      </Box>
    </Dialog>
  );
}
